import { InertiaLinkProps, Link } from '@inertiajs/react';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Spinner } from './Spinner';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground shadow',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        success: 'border-transparent bg-success text-success-foreground',
        warning: 'border-transparent bg-warning text-warning-foreground',
        informative: 'border-transparent bg-informative text-informative-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground shadow',
        outline: 'text-foreground'
      },
      interactive: {
        true: 'cursor-pointer',
        false: 'cursor-default'
      }
    },
    compoundVariants: [
      {
        variant: 'default',
        interactive: true,
        className: 'hover:bg-primary/80'
      },
      {
        variant: 'secondary',
        interactive: true,
        className: 'hover:bg-secondary/80'
      },
      {
        variant: 'success',
        interactive: true,
        className: 'hover:bg-success/80'
      },
      {
        variant: 'warning',
        interactive: true,
        className: 'hover:bg-warning/80'
      },
      {
        variant: 'informative',
        interactive: true,
        className: 'hover:bg-informative/80'
      },
      {
        variant: 'destructive',
        interactive: true,
        className: 'hover:bg-destructive/80'
      }
    ],
    defaultVariants: {
      variant: 'default',
      interactive: false
    }
  }
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    Omit<VariantProps<typeof badgeVariants>, 'interactive'> {}

function Badge({ className, variant, onClick, onKeyDown, ...props }: BadgeProps) {
  const isInteractive = !!onClick;
  const interactiveProps: React.HTMLAttributes<HTMLSpanElement> = isInteractive
    ? {
        role: 'button',
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.currentTarget.click();
          }
          onKeyDown?.(e);
        }
      }
    : { onKeyDown };
  return (
    <span
      data-slot="badge"
      data-interactive={isInteractive || undefined}
      data-variant={variant ?? 'default'}
      className={cn(badgeVariants({ variant, interactive: isInteractive }), className)}
      {...interactiveProps}
      {...props}
    />
  );
}

export type LinkBadgeProps = Omit<VariantProps<typeof badgeVariants>, 'interactive'> &
  Omit<InertiaLinkProps, 'size'> & {
    loading?: boolean;
  };

const LinkBadge = ({
  className,
  loading,
  variant = 'default',
  ref,
  ...props
}: LinkBadgeProps & { ref?: React.Ref<HTMLAnchorElement> }) => {
  return (
    <Link
      data-slot="badge"
      data-variant={variant ?? 'default'}
      className={cn(badgeVariants({ variant, interactive: true }), className)}
      ref={ref}
      {...props}
    >
      {loading ? <Spinner size="md" inline /> : null}
      {props.children}
    </Link>
  );
};
LinkBadge.displayName = 'LinkBadge';

export { Badge, LinkBadge };
